import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  Node,
  Edge,
  Connection,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  NodeChange,
  EdgeChange,
} from '@xyflow/react';
import {
  NodeExecutionStatus,
  WorkflowCollaborator,
  WorkflowCollaboratorProfile,
} from 'types';
import { toast } from 'sonner';

// Data types for handle compatibility checking
export type HandleDataType = 'text' | 'image' | 'video' | 'audio' | 'unknown';

// Determine data type from handle ID
export function getDataTypeFromHandle(
  handleId: string | null | undefined,
): HandleDataType {
  if (!handleId) return 'unknown';

  const id = handleId.toLowerCase();
  if (
    id.includes('image') ||
    id === 'first-frame' ||
    id === 'last-frame' ||
    id === 'references' ||
    id === 'reference'
  )
    return 'image';
  if (id.includes('video')) return 'video';
  if (id.includes('audio') || id.includes('speech') || id.includes('sound'))
    return 'audio';
  if (
    id.includes('text') ||
    id.includes('prompt') ||
    id.includes('caption') ||
    id.includes('script')
  )
    return 'text';
  return 'unknown';
}

// Check if two data types are compatible for connection
function areTypesCompatible(
  sourceType: HandleDataType,
  targetType: HandleDataType,
): boolean {
  // Unknown types are always compatible (fallback for generic handles)
  if (sourceType === 'unknown' || targetType === 'unknown') return true;
  // Same types are compatible
  return sourceType === targetType;
}

interface HistorySnapshot {
  nodes: Node[];
  edges: Edge[];
}

interface WorkflowState {
  // React Flow state
  nodes: Node[];
  edges: Edge[];
  selectedNode: string | null;
  nextNodeSequence: number;

  // History state
  past: HistorySnapshot[];
  future: HistorySnapshot[];
  canUndo: boolean;
  canRedo: boolean;

  // Workflow metadata
  workflowId: string | null;
  workflowName: string;
  workflowOwnerId: string | null;
  workflowOwnerProfile: WorkflowCollaboratorProfile | null;
  shareWithTeam: boolean;
  isTeamMember: boolean;
  isSaving: boolean;
  hasUnsavedChanges: boolean;

  // Execution state
  isExecuting: boolean;
  executionId: string | null;
  nodeStatuses: Record<string, NodeExecutionStatus>;
  nodeResults: Record<string, any>;
  nodeErrors: Record<string, string>;
  collaborators: WorkflowCollaborator[];

  // Actions
  setNodes: (nodes: Node[]) => void;
  setEdges: (edges: Edge[]) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  addNode: (node: Node) => void;
  addNodeAndConnect: (node: Node, connection: Connection) => void;
  updateNodeData: (nodeId: string, data: any) => void;
  deleteNode: (nodeId: string) => void;
  duplicateNode: (nodeId: string) => void;
  setSelectedNode: (nodeId: string | null) => void;
  groupNodes: (nodeIds: string[]) => void;
  ungroupNodes: (groupId: string) => void;

  // History actions
  undo: () => void;
  redo: () => void;
  saveToHistory: () => void;

  // Workflow actions
  setWorkflowId: (id: string | null) => void;
  setWorkflowName: (name: string) => void;
  setWorkflowOwnerId: (id: string | null) => void;
  setWorkflowOwnerProfile: (
    profile: WorkflowCollaboratorProfile | null,
  ) => void;
  loadWorkflow: (
    nodes: Node[],
    edges: Edge[],
    id: string,
    name: string,
    ownerId: string,
  ) => void;
  setShareWithTeam: (shareWithTeam: boolean) => void;
  setIsTeamMember: (isTeamMember: boolean) => void;
  clearWorkflow: () => void;
  markSaved: () => void;
  markUnsaved: () => void;

  // Execution actions
  startExecution: (executionId: string) => void;
  stopExecution: () => void;
  setNodeStatus: (nodeId: string, status: NodeExecutionStatus) => void;
  setNodeResult: (nodeId: string, result: any) => void;
  setNodeError: (nodeId: string, error: string) => void;
  clearExecutionState: () => void;
  setCollaborators: (collaborators: WorkflowCollaborator[]) => void;
  upsertCollaborator: (collaborator: WorkflowCollaborator) => void;
  removeCollaboratorLocal: (userId: string) => void;
}

// Sorts nodes so group parents appear before their children (required by React Flow)
function sortNodesForReactFlow(nodes: Node[]): Node[] {
  const groups: Node[] = [];
  const childrenMap = new Map<string, Node[]>();
  const others: Node[] = [];

  for (const node of nodes) {
    if (node.type === 'group') {
      groups.push(node);
    } else if (node.parentId) {
      const siblings = childrenMap.get(node.parentId) || [];
      siblings.push(node);
      childrenMap.set(node.parentId, siblings);
    } else {
      others.push(node);
    }
  }

  const result: Node[] = [...others];
  for (const group of groups) {
    result.push(group);
    const groupChildren = childrenMap.get(group.id) || [];
    result.push(...groupChildren);
  }

  return result;
}

const MAX_HISTORY_SIZE = 50;

// Applies a Connection's edge + side-effects to state (single-input cleanup,
// auto-mention insertion). Caller is responsible for any history snapshot and
// `hasUnsavedChanges` updates so that grouped operations stay atomic.
function applyConnectionToState(state: any, connection: Connection) {
  // Limit certain nodes to a single image input per handle
  if (connection.target && connection.targetHandle) {
    const targetNode = state.nodes.find(
      (n: Node) => n.id === connection.target,
    );
    const nodeType = targetNode?.type as string | undefined;
    const handleId = connection.targetHandle as string;

    const isSingleImageInputNode =
      nodeType === 'image-upscaler' || nodeType === 'image-to-video';
    const isFirstLastFrameNode = nodeType === 'first-last-frame-video';
    const isTalkingVideoNode = nodeType === 'talking-video';

    const isLimitedHandle =
      (isSingleImageInputNode && handleId === 'image') ||
      (isFirstLastFrameNode &&
        (handleId === 'first-frame' || handleId === 'last-frame')) ||
      (isTalkingVideoNode &&
        (handleId === 'image' ||
          handleId === 'video' ||
          handleId === 'audio' ||
          handleId === 'text'));

    if (isLimitedHandle) {
      // Remove any existing edges into this specific handle
      state.edges = state.edges.filter(
        (e: Edge) =>
          !(
            e.target === connection.target &&
            e.targetHandle === connection.targetHandle
          ),
      );
    }
  }

  state.edges = addEdge(connection, state.edges);

  // Auto-insert @NodeName for text/prompt handles
  if (
    connection.targetHandle === 'prompt' ||
    connection.targetHandle === 'text' ||
    connection.targetHandle === 'extra1' ||
    connection.targetHandle === 'extra2'
  ) {
    const sourceNode = state.nodes.find(
      (n: Node) => n.id === connection.source,
    );
    const targetNode = state.nodes.find(
      (n: Node) => n.id === connection.target,
    );

    if (sourceNode && targetNode) {
      const sourceName = (sourceNode.data?.label as string) || 'Node';
      const targetConfig = (targetNode.data?.config as any) || {};
      const mention = `@${sourceName}`;

      // Only auto-insert into prompt for prompt/text handles
      if (
        connection.targetHandle === 'prompt' ||
        connection.targetHandle === 'text'
      ) {
        const currentPrompt = targetConfig.prompt || '';

        if (!currentPrompt.includes(mention)) {
          const updatedPrompt = currentPrompt
            ? `${mention} ${currentPrompt}`
            : mention;

          state.nodes = state.nodes.map((node: Node) => {
            if (node.id === connection.target) {
              return {
                ...node,
                data: {
                  ...node.data,
                  config: {
                    ...targetConfig,
                    prompt: updatedPrompt,
                  },
                },
              };
            }
            return node;
          });
        }
      }

      // Also auto-insert into text preset field values (keywords is mapped to handle "text")
      if (
        targetNode.type === 'text-preset' &&
        (connection.targetHandle === 'text' ||
          connection.targetHandle === 'extra1' ||
          connection.targetHandle === 'extra2')
      ) {
        const fieldsValue = targetConfig.fields;
        const fieldsMap =
          fieldsValue instanceof Map
            ? new Map(fieldsValue)
            : new Map(Object.entries(fieldsValue || {}));

        const fieldName =
          connection.targetHandle === 'text'
            ? 'keywords'
            : (connection.targetHandle as string);
        const currentValue = fieldsMap.get(fieldName) || '';
        const mentionInsert = `@${sourceName}`;

        if (!String(currentValue).includes(mentionInsert)) {
          const updatedValue = currentValue
            ? `${mentionInsert} ${currentValue}`
            : mentionInsert;
          fieldsMap.set(fieldName, updatedValue);

          state.nodes = state.nodes.map((node: Node) => {
            if (node.id === connection.target) {
              return {
                ...node,
                data: {
                  ...node.data,
                  config: {
                    ...targetConfig,
                    fields: Object.fromEntries(fieldsMap.entries()),
                  },
                },
              };
            }
            return node;
          });
        }
      }
    }
  }
}

export const useWorkflowStore = create<WorkflowState>()(
  immer((set, get) => ({
    // Initial state
    nodes: [],
    edges: [],
    selectedNode: null,
    nextNodeSequence: 1,
    past: [],
    future: [],
    canUndo: false,
    canRedo: false,
    workflowId: null,
    workflowName: 'Untitled Workflow',
    workflowOwnerId: null,
    workflowOwnerProfile: null,
    shareWithTeam: false,
    isTeamMember: false,
    isSaving: false,
    hasUnsavedChanges: false,
    isExecuting: false,
    executionId: null,
    nodeStatuses: {},
    nodeResults: {},
    nodeErrors: {},
    collaborators: [],

    // React Flow actions
    setNodes: (nodes) =>
      set((state) => {
        state.nodes = nodes;
        state.hasUnsavedChanges = true;
      }),

    setEdges: (edges) =>
      set((state) => {
        state.edges = edges;
        state.hasUnsavedChanges = true;
      }),

    onNodesChange: (changes) =>
      set((state) => {
        // Collect IDs of locked groups and their children
        const lockedGroupIds = new Set<string>();
        const lockedChildIds = new Set<string>();
        for (const node of state.nodes) {
          if (
            node.type === 'group' &&
            (node.data as { locked?: boolean })?.locked
          ) {
            lockedGroupIds.add(node.id);
          }
        }
        if (lockedGroupIds.size > 0) {
          for (const node of state.nodes) {
            if (node.parentId && lockedGroupIds.has(node.parentId)) {
              lockedChildIds.add(node.id);
            }
          }
        }

        // Filter out position/dimension changes for locked groups and their children
        const filteredChanges = changes.filter((change) => {
          if (change.type === 'position') {
            const id = change.id;
            if (lockedGroupIds.has(id) || lockedChildIds.has(id)) return false;
          }
          if (change.type === 'dimensions') {
            const id = change.id;
            if (lockedGroupIds.has(id)) return false;
          }
          // Prevent deleting locked groups via keyboard (backspace/delete)
          if (change.type === 'remove') {
            if (lockedGroupIds.has(change.id)) return false;
          }
          return true;
        });

        // Only save to history when drag ends
        if (
          filteredChanges.some(
            (change) => change.type === 'position' && change.dragging === false,
          )
        ) {
          // Save current state to history before applying changes
          const snapshot = { nodes: state.nodes, edges: state.edges };
          state.past.push(snapshot);
          if (state.past.length > MAX_HISTORY_SIZE) {
            state.past.shift();
          }
          state.future = [];
          state.canUndo = state.past.length > 0;
          state.canRedo = false;
        }

        state.nodes = applyNodeChanges(filteredChanges, state.nodes);
        state.hasUnsavedChanges = true;
      }),

    onEdgesChange: (changes) =>
      set((state) => {
        // Save to history for edge removals
        if (changes.some((change) => change.type === 'remove')) {
          const snapshot = { nodes: state.nodes, edges: state.edges };
          state.past.push(snapshot);
          if (state.past.length > MAX_HISTORY_SIZE) {
            state.past.shift();
          }
          state.future = [];
          state.canUndo = state.past.length > 0;
          state.canRedo = false;
        }

        state.edges = applyEdgeChanges(changes, state.edges);
        state.hasUnsavedChanges = true;
      }),

    onConnect: (connection) =>
      set((state) => {
        // Prevent self-connections (connecting a node to itself)
        if (connection.source === connection.target) {
          toast.error('Cannot connect a node to itself.');
          return;
        }

        // Check data type compatibility before connecting
        const sourceType = getDataTypeFromHandle(connection.sourceHandle);
        const targetType = getDataTypeFromHandle(connection.targetHandle);

        if (!areTypesCompatible(sourceType, targetType)) {
          toast.error(
            `Cannot connect ${sourceType} output to ${targetType} input. Data types must match.`,
          );
          return;
        }

        // Save to history before adding edge
        const snapshot = { nodes: state.nodes, edges: state.edges };
        state.past.push(snapshot);
        if (state.past.length > MAX_HISTORY_SIZE) {
          state.past.shift();
        }
        state.future = [];
        state.canUndo = state.past.length > 0;
        state.canRedo = false;

        applyConnectionToState(state, connection);

        state.hasUnsavedChanges = true;
      }),

    addNode: (node) =>
      set((state) => {
        // Save to history before adding node
        const snapshot = { nodes: state.nodes, edges: state.edges };
        state.past.push(snapshot);
        if (state.past.length > MAX_HISTORY_SIZE) {
          state.past.shift();
        }
        state.future = [];
        state.canUndo = state.past.length > 0;
        state.canRedo = false;

        // Prefix label with a sequential number on add (user can rename later)
        const baseLabel =
          (node.data && (node.data as any).label) ||
          (typeof node.type === 'string' ? node.type : 'Node');
        const nextNumber = state.nextNodeSequence ?? 1;
        const numberedLabel = `#${nextNumber} ${String(baseLabel)}`;
        if (!node.data) {
          (node as any).data = {};
        }
        (node.data as any).label = numberedLabel;

        // Increment sequence for next node
        state.nextNodeSequence = nextNumber + 1;

        state.nodes.push(node);
        state.hasUnsavedChanges = true;
      }),

    addNodeAndConnect: (node, connection) =>
      set((state) => {
        if (connection.source === connection.target) {
          toast.error('Cannot connect a node to itself.');
          return;
        }

        const sourceType = getDataTypeFromHandle(connection.sourceHandle);
        const targetType = getDataTypeFromHandle(connection.targetHandle);
        if (!areTypesCompatible(sourceType, targetType)) {
          toast.error(
            `Cannot connect ${sourceType} output to ${targetType} input. Data types must match.`,
          );
          return;
        }

        // Single history snapshot covers both the node add and the edge add so
        // an undo reverts the whole "drag-to-create" gesture in one step.
        const snapshot = { nodes: state.nodes, edges: state.edges };
        state.past.push(snapshot);
        if (state.past.length > MAX_HISTORY_SIZE) {
          state.past.shift();
        }
        state.future = [];
        state.canUndo = state.past.length > 0;
        state.canRedo = false;

        // Apply numbered-label convention (matches addNode)
        const baseLabel =
          (node.data && (node.data as any).label) ||
          (typeof node.type === 'string' ? node.type : 'Node');
        const nextNumber = state.nextNodeSequence ?? 1;
        const numberedLabel = `#${nextNumber} ${String(baseLabel)}`;
        if (!node.data) {
          (node as any).data = {};
        }
        (node.data as any).label = numberedLabel;
        state.nextNodeSequence = nextNumber + 1;

        state.nodes.push(node);
        applyConnectionToState(state, connection);
        state.hasUnsavedChanges = true;
      }),

    updateNodeData: (nodeId, data) =>
      set((state) => {
        const node = state.nodes.find((n) => n.id === nodeId);
        if (node) {
          // Only save to history if this is not an execution-related update
          const isExecutionUpdate =
            data.status !== undefined ||
            data.result !== undefined ||
            data.error !== undefined;

          if (!isExecutionUpdate) {
            const snapshot = { nodes: state.nodes, edges: state.edges };
            state.past.push(snapshot);
            if (state.past.length > MAX_HISTORY_SIZE) {
              state.past.shift();
            }
            state.future = [];
            state.canUndo = state.past.length > 0;
            state.canRedo = false;
          }

          node.data = { ...node.data, ...data };
          // Keep nodeResults in sync so cached execution results reflect the latest value
          if (data.result !== undefined) {
            state.nodeResults[nodeId] = data.result;
          }
          state.hasUnsavedChanges = true;
        }
      }),

    deleteNode: (nodeId) =>
      set((state) => {
        // Save to history before deleting
        const snapshot = { nodes: state.nodes, edges: state.edges };
        state.past.push(snapshot);
        if (state.past.length > MAX_HISTORY_SIZE) {
          state.past.shift();
        }
        state.future = [];
        state.canUndo = state.past.length > 0;
        state.canRedo = false;

        // If deleting a group, ungroup children first (non-destructive)
        const nodeToDelete = state.nodes.find((n) => n.id === nodeId);
        if (nodeToDelete?.type === 'group') {
          state.nodes.forEach((n) => {
            if (n.parentId === nodeId) {
              n.position = {
                x: n.position.x + nodeToDelete.position.x,
                y: n.position.y + nodeToDelete.position.y,
              };
              delete (n as any).parentId;
            }
          });
        }

        state.nodes = state.nodes.filter((n) => n.id !== nodeId);
        state.edges = state.edges.filter(
          (e) => e.source !== nodeId && e.target !== nodeId,
        );
        state.hasUnsavedChanges = true;
        if (state.selectedNode === nodeId) {
          state.selectedNode = null;
        }
      }),

    duplicateNode: (nodeId) =>
      set((state) => {
        const nodeToDuplicate = state.nodes.find((n) => n.id === nodeId);
        if (!nodeToDuplicate) return;

        // Save to history before duplicating
        const snapshot = { nodes: state.nodes, edges: state.edges };
        state.past.push(snapshot);
        if (state.past.length > MAX_HISTORY_SIZE) {
          state.past.shift();
        }
        state.future = [];
        state.canUndo = state.past.length > 0;
        state.canRedo = false;

        // Generate new ID
        const newId = `${nodeToDuplicate.type}-${Date.now()}`;

        // Create duplicate with offset position
        const duplicatedNode: Node = {
          ...nodeToDuplicate,
          id: newId,
          position: {
            x: nodeToDuplicate.position.x + 50,
            y: nodeToDuplicate.position.y + 50,
          },
          data: {
            ...nodeToDuplicate.data,
            // Add " (Copy)" to label if it exists
            label: nodeToDuplicate.data.label
              ? `${nodeToDuplicate.data.label} (Copy)`
              : undefined,
            // Clear execution state
            status: undefined,
            result: undefined,
            error: undefined,
          },
          selected: false,
        };

        state.nodes.push(duplicatedNode);

        // If duplicating a group, also duplicate children and internal edges
        if (nodeToDuplicate.type === 'group') {
          const children = state.nodes.filter(
            (n) => n.parentId === nodeId && n.id !== newId,
          );
          const childIdMap = new Map<string, string>();

          const newChildren = children.map((child) => {
            const newChildId = `${child.type}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
            childIdMap.set(child.id, newChildId);
            return {
              ...child,
              id: newChildId,
              parentId: newId,
              data: {
                ...child.data,
                label: child.data.label
                  ? `${child.data.label} (Copy)`
                  : undefined,
                status: undefined,
                result: undefined,
                error: undefined,
              },
              selected: false,
            } as Node;
          });
          state.nodes.push(...newChildren);

          // Duplicate edges between children
          const internalEdges = state.edges.filter(
            (e) => childIdMap.has(e.source) && childIdMap.has(e.target),
          );
          const newEdges = internalEdges.map((e) => ({
            ...e,
            id: `e-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
            source: childIdMap.get(e.source)!,
            target: childIdMap.get(e.target)!,
          }));
          state.edges.push(...newEdges);

          // Re-sort to ensure parents before children
          state.nodes = sortNodesForReactFlow(state.nodes);
        }

        state.hasUnsavedChanges = true;
      }),

    setSelectedNode: (nodeId) =>
      set((state) => {
        state.selectedNode = nodeId;
      }),

    groupNodes: (nodeIds) =>
      set((state) => {
        // Filter out group nodes and nodes already in a group (no nesting)
        const validIds = nodeIds.filter((id) => {
          const node = state.nodes.find((n) => n.id === id);
          return node && node.type !== 'group' && !node.parentId;
        });

        if (validIds.length < 2) {
          toast.error('Select at least 2 nodes to group.');
          return;
        }

        // Save to history
        const snapshot = { nodes: state.nodes, edges: state.edges };
        state.past.push(snapshot);
        if (state.past.length > MAX_HISTORY_SIZE) {
          state.past.shift();
        }
        state.future = [];
        state.canUndo = state.past.length > 0;
        state.canRedo = false;

        // Calculate bounding box of selected nodes
        const selectedNodes = state.nodes.filter((n) =>
          validIds.includes(n.id),
        );
        const PADDING_X = 20;
        const PADDING_TOP = 50; // extra space for label bar
        const PADDING_BOTTOM = 20;
        const DEFAULT_NODE_WIDTH = 280;
        const DEFAULT_NODE_HEIGHT = 200;

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        for (const node of selectedNodes) {
          const w = (node.measured as any)?.width ?? DEFAULT_NODE_WIDTH;
          const h = (node.measured as any)?.height ?? DEFAULT_NODE_HEIGHT;
          minX = Math.min(minX, node.position.x);
          minY = Math.min(minY, node.position.y);
          maxX = Math.max(maxX, node.position.x + w);
          maxY = Math.max(maxY, node.position.y + h);
        }

        const groupX = minX - PADDING_X;
        const groupY = minY - PADDING_TOP;
        const groupWidth = maxX - minX + PADDING_X * 2;
        const groupHeight = maxY - minY + PADDING_TOP + PADDING_BOTTOM;

        const groupId = `group-${Date.now()}`;
        const groupNode: Node = {
          id: groupId,
          type: 'group',
          position: { x: groupX, y: groupY },
          data: {
            label: 'Group',
            style: { width: groupWidth, height: groupHeight },
          },
          style: { width: groupWidth, height: groupHeight },
        };

        // Update children: set parentId, convert to relative position
        for (const node of selectedNodes) {
          (node as any).parentId = groupId;
          node.position = {
            x: node.position.x - groupX,
            y: node.position.y - groupY,
          };
        }

        state.nodes.push(groupNode);

        // Re-sort to ensure parents before children
        state.nodes = sortNodesForReactFlow(state.nodes);
        state.hasUnsavedChanges = true;
      }),

    ungroupNodes: (groupId) =>
      set((state) => {
        const groupNode = state.nodes.find(
          (n) => n.id === groupId && n.type === 'group',
        );
        if (!groupNode) return;

        // Save to history
        const snapshot = { nodes: state.nodes, edges: state.edges };
        state.past.push(snapshot);
        if (state.past.length > MAX_HISTORY_SIZE) {
          state.past.shift();
        }
        state.future = [];
        state.canUndo = state.past.length > 0;
        state.canRedo = false;

        // Convert children to absolute positions and remove parentId
        state.nodes.forEach((n) => {
          if (n.parentId === groupId) {
            n.position = {
              x: n.position.x + groupNode.position.x,
              y: n.position.y + groupNode.position.y,
            };
            delete (n as any).parentId;
          }
        });

        // Remove the group node
        state.nodes = state.nodes.filter((n) => n.id !== groupId);
        state.hasUnsavedChanges = true;
      }),

    // History actions
    undo: () =>
      set((state) => {
        if (state.past.length === 0) return;

        const previous = state.past.pop()!;
        const current = { nodes: state.nodes, edges: state.edges };
        state.future.push(current);

        state.nodes = previous.nodes;
        state.edges = previous.edges;
        state.canUndo = state.past.length > 0;
        state.canRedo = true;
        state.hasUnsavedChanges = true;
      }),

    redo: () =>
      set((state) => {
        if (state.future.length === 0) return;

        const next = state.future.pop()!;
        const current = { nodes: state.nodes, edges: state.edges };
        state.past.push(current);

        state.nodes = next.nodes;
        state.edges = next.edges;
        state.canUndo = true;
        state.canRedo = state.future.length > 0;
        state.hasUnsavedChanges = true;
      }),

    saveToHistory: () =>
      set((state) => {
        const snapshot = { nodes: state.nodes, edges: state.edges };
        state.past.push(snapshot);
        if (state.past.length > MAX_HISTORY_SIZE) {
          state.past.shift();
        }
        state.future = [];
        state.canUndo = state.past.length > 0;
        state.canRedo = false;
      }),

    // Workflow actions
    setWorkflowId: (id) =>
      set((state) => {
        state.workflowId = id;
      }),

    setWorkflowName: (name) =>
      set((state) => {
        state.workflowName = name;
        state.hasUnsavedChanges = true;
      }),

    setWorkflowOwnerId: (id) =>
      set((state) => {
        state.workflowOwnerId = id;
        if (id === null) {
          state.workflowOwnerProfile = null;
        }
      }),

    setWorkflowOwnerProfile: (profile) =>
      set((state) => {
        state.workflowOwnerProfile = profile;
      }),

    loadWorkflow: (nodes, edges, id, name, ownerId) =>
      set((state) => {
        // Sort nodes so group parents appear before their children
        state.nodes = sortNodesForReactFlow(nodes);
        state.edges = edges;
        state.workflowId = id;
        state.workflowName = name;
        state.workflowOwnerId = ownerId;
        // Initialize next sequence number from existing labels, fallback to nodes length + 1
        const regex = /^#(\d+)\s/;
        const maxFound =
          nodes
            .map((n) => {
              const label = (n.data as any)?.label as string | undefined;
              const m = label ? regex.exec(label) : null;
              return m ? parseInt(m[1] || '0', 10) : 0;
            })
            .reduce((a, b) => Math.max(a, b), 0) || 0;
        state.nextNodeSequence = Math.max(maxFound + 1, nodes.length + 1);
        state.hasUnsavedChanges = false;
        state.selectedNode = null;
        // Clear history when loading a workflow
        state.past = [];
        state.future = [];
        state.canUndo = false;
        state.canRedo = false;
      }),

    setShareWithTeam: (shareWithTeam) =>
      set((state) => {
        state.shareWithTeam = shareWithTeam;
      }),

    setIsTeamMember: (isTeamMember) =>
      set((state) => {
        state.isTeamMember = isTeamMember;
      }),

    clearWorkflow: () =>
      set((state) => {
        state.nodes = [];
        state.edges = [];
        state.workflowId = null;
        state.workflowName = 'Untitled Workflow';
        state.workflowOwnerId = null;
        state.workflowOwnerProfile = null;
        state.shareWithTeam = false;
        state.isTeamMember = false;
        state.nextNodeSequence = 1;
        state.hasUnsavedChanges = false;
        state.selectedNode = null;
        state.nodeStatuses = {};
        state.nodeResults = {};
        state.nodeErrors = {};
        // Clear history when clearing workflow
        state.past = [];
        state.future = [];
        state.canUndo = false;
        state.canRedo = false;
      }),

    markSaved: () =>
      set((state) => {
        state.hasUnsavedChanges = false;
        state.isSaving = false;
      }),

    markUnsaved: () =>
      set((state) => {
        state.hasUnsavedChanges = true;
      }),

    // Execution actions
    startExecution: (executionId) =>
      set((state) => {
        state.isExecuting = true;
        state.executionId = executionId;
        state.nodeStatuses = {};
        state.nodeResults = {};
        state.nodeErrors = {};
      }),

    stopExecution: () =>
      set((state) => {
        state.isExecuting = false;
      }),

    setNodeStatus: (nodeId, status) =>
      set((state) => {
        state.nodeStatuses[nodeId] = status;

        // Also update the node's visual state
        const node = state.nodes.find((n) => n.id === nodeId);
        if (node) {
          const newData: any = { ...node.data, status };
          // When a node starts running again, clear any previous error
          if (status === 'running') {
            delete state.nodeErrors[nodeId];
            delete newData.error;
          }
          node.data = newData;
        }
      }),

    setNodeResult: (nodeId, result) =>
      set((state) => {
        state.nodeResults[nodeId] = result;
        // Clear any previous error when we have a new result
        delete state.nodeErrors[nodeId];

        // Update the node's data
        const node = state.nodes.find((n) => n.id === nodeId);
        if (node) {
          const newData: any = { ...node.data, result };
          delete newData.error;
          node.data = newData;
        }
      }),

    setNodeError: (nodeId, error) =>
      set((state) => {
        state.nodeErrors[nodeId] = error;

        // Update the node's data
        const node = state.nodes.find((n) => n.id === nodeId);
        if (node) {
          node.data = { ...node.data, error };
        }
      }),

    clearExecutionState: () =>
      set((state) => {
        state.isExecuting = false;
        state.executionId = null;
        state.nodeStatuses = {};
        state.nodeResults = {};
        state.nodeErrors = {};

        // Clear execution state from nodes
        state.nodes = state.nodes.map((node) => ({
          ...node,
          data: {
            ...node.data,
            status: undefined,
            result: undefined,
            error: undefined,
          },
        }));
      }),
    setCollaborators: (collaborators) =>
      set((state) => {
        state.collaborators = collaborators;
      }),
    upsertCollaborator: (collaborator) =>
      set((state) => {
        const index = state.collaborators.findIndex(
          (c) => c.user_id === collaborator.user_id,
        );
        if (index >= 0) {
          state.collaborators[index] = collaborator;
        } else {
          state.collaborators.push(collaborator);
        }
      }),
    removeCollaboratorLocal: (userId) =>
      set((state) => {
        state.collaborators = state.collaborators.filter(
          (c) => c.user_id !== userId,
        );
      }),
  })),
);
